import type { IndustrySetupContext, IndustrySetupInput, IndustrySetupSelections } from "./types";
import { chooseExistingSetupOption } from "./personalTrainerSelections";

export type SalonSetup = NonNullable<IndustrySetupInput["salonSetup"]>;
export type SalonStaff = SalonSetup["staff"][number];
export type SalonService = SalonSetup["services"][number];
export type SalonHours = SalonStaff["workingHours"][number];

export function defaultSalonHours(): SalonHours[] {
  return [1, 2, 3, 4, 5].map((dayOfWeek) => ({ dayOfWeek, startTime: "09:00", endTime: "17:00" }));
}

export function createSalonInput(displayName: string, t: (key: string) => string): IndustrySetupInput {
  return {
    displayName,
    installOnly: false,
    selectedRecommendedAppCodes: null,
    salonSetup: {
      staff: [{ key: "staff-1", name: "", workingHours: defaultSalonHours() }],
      services: [{ key: "service-1", name: t("hairSalon.defaultService"), durationMinutes: 30, price: null, staffKeys: ["staff-1"] }],
    },
  };
}

function validName(name: string) {
  return name.trim().length > 0 && name.trim().length <= 255;
}

function uniqueItems(items: { key: string; name: string }[]) {
  return items.every((item) => /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(item.key) && validName(item.name))
    && new Set(items.map((item) => item.key)).size === items.length
    && new Set(items.map((item) => item.name.trim().toLowerCase())).size === items.length;
}

export function validSalonStaff(setup: SalonSetup | null | undefined): boolean {
  if (!setup || setup.staff.length < 1 || setup.staff.length > 30 || !uniqueItems(setup.staff)) return false;
  const time = /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/;
  return setup.staff.every((person) => person.workingHours.length > 0 && person.workingHours.length <= 14
    && person.workingHours.every((hours, index) => (
      Number.isInteger(hours.dayOfWeek) && hours.dayOfWeek >= 1 && hours.dayOfWeek <= 7
      && time.test(hours.startTime) && time.test(hours.endTime) && hours.startTime < hours.endTime
      && !person.workingHours.slice(index + 1).some((other) => other.dayOfWeek === hours.dayOfWeek
        && other.startTime < hours.endTime && hours.startTime < other.endTime)
    )));
}

export function validSalonSetup(input: IndustrySetupInput): boolean {
  if (input.installOnly) return true;
  const setup = input.salonSetup;
  if (!setup || !validSalonStaff(setup) || setup.services.length < 1 || setup.services.length > 50 || !uniqueItems(setup.services)) return false;
  const keys = new Set(setup.staff.map((person) => person.key));
  return setup.services.every((service) => Number.isInteger(service.durationMinutes)
    && service.durationMinutes >= 5 && service.durationMinutes <= 480
    && (service.price == null || /^[0-9]{1,8}(?:\.[0-9]{1,2})?$/.test(service.price))
    && service.staffKeys.length > 0 && service.staffKeys.every((key) => keys.has(key))
    && new Set(service.staffKeys).size === service.staffKeys.length)
    && setup.staff.every((person) => setup.services.some((service) => service.staffKeys.includes(person.key)));
}

export function removeSalonStaff(setup: SalonSetup, key: string): SalonSetup {
  return {
    staff: setup.staff.filter((person) => person.key !== key),
    services: setup.services.map((service) => ({ ...service, staffKeys: service.staffKeys.filter((staffKey) => staffKey !== key) })),
  };
}

export function resolveSalonSelections(context: IndustrySetupContext, input: IndustrySetupInput) {
  const companyUuid = chooseExistingSetupOption(context.companies, input.companyUuid ?? context.companyUuid);
  const branches = companyUuid ? context.branches.filter((branch) => branch.parentUuid === companyUuid) : [];
  const branchUuid = chooseExistingSetupOption(branches, input.branchUuid ?? context.branchUuid);
  const selections: IndustrySetupSelections = {
    companyUuid,
    branchUuid,
    // The server resumes the ledger for the selected branch. Never carry another branch's portal.
    bookingPortalUuid: null,
    coachProfileUuid: null,
    bookingResourceUuid: null,
    bookingServiceUuid: null,
    clientPortalUuid: null,
  };
  const groups = [
    { field: "companyUuid" as const, options: context.companies, value: companyUuid },
    { field: "branchUuid" as const, options: branches, value: branchUuid },
  ];
  return { selections, groups, needsSelection: groups.some((group) => group.options.length > 1 && group.value === null) };
}

/** Keep incomplete user edits when resuming, but reject malformed browser storage. */
export function parseSalonSetupDraft(value: unknown): SalonSetup | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const setup = value as Record<string, unknown>;
  if (!Array.isArray(setup.staff) || !Array.isArray(setup.services) || setup.staff.length > 30 || setup.services.length > 50) return undefined;
  const object = (item: unknown): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item);
  if (!setup.staff.every((person) => object(person) && typeof person.key === "string" && typeof person.name === "string"
    && Array.isArray(person.workingHours) && person.workingHours.length <= 14
    && person.workingHours.every((hours: unknown) => object(hours) && typeof hours.dayOfWeek === "number"
      && typeof hours.startTime === "string" && typeof hours.endTime === "string"))) return undefined;
  if (!setup.services.every((service) => object(service) && typeof service.key === "string" && typeof service.name === "string"
    && typeof service.durationMinutes === "number" && (service.price == null || typeof service.price === "string")
    && Array.isArray(service.staffKeys) && service.staffKeys.length <= 30 && service.staffKeys.every((key: unknown) => typeof key === "string"))) return undefined;
  return value as SalonSetup;
}
